import * as Y from "yjs";
import { type Brand, newId } from "./ids.ts";

/**
 * Datamodell för Centralspec — svensk elcentral-specifikation.
 *
 * En `Panel` representerar en elcentral (huvudcentral, grupp-central).
 * En `Section` är en sektionering (S1, S2, …) inom centralen som
 * rader grupperas under. En `PanelRow` är en rad i tabellen — ofta en
 * säkring/dvärgbrytare/JFB med tillhörande kabel och användnings-etikett.
 *
 * Persistens via Y.Map i samma Y.Doc som schema-editorn kan använda, men i
 * centralspec-appen har varje panel en egen Y.Doc (ett lokalt IndexedDB-dok).
 */

export type PanelId = Brand<string, "PanelId">;
export type SectionId = Brand<string, "SectionId">;
export type PanelRowId = Brand<string, "PanelRowId">;

export const panelId = (s: string) => s as PanelId;
export const sectionId = (s: string) => s as SectionId;
export const panelRowId = (s: string) => s as PanelRowId;

/** Karakteristik för dvärgbrytare (B = vanlig, C = motor, D = hög utlösningsström). */
export type BreakerCharacteristic = "B" | "C" | "D";

/** JFB-typer enligt IEC 62423. A = pulserande DC, B = ren DC, F = frekvens. */
export type RcdType = "AC" | "A" | "B" | "F";

/** Jordningssystem enligt SS 436 40 00. Vanligast i Sverige är TN-C-S. */
export type EarthingSystem = "TN-S" | "TN-C" | "TN-C-S" | "TT" | "IT";

/** Projektstatus — speglar enlinje.se:s granskningsflöde. */
export type PanelStatus = "draft" | "review" | "done";

/**
 * Last-typ på en rad. Används för auto-säkring: motorer får C-karakteristik,
 * resistiva laster (spis/värmare) räknas med cosφ 1,0, medan motor/EV-laddare
 * räknas med cosφ 0,85.
 */
export type RowLoadType =
  | "lamp"         // ljuspunkt (LED-spot, armatur)
  | "socket"       // uttag (230 V 10 A)
  | "motor"        // motor (kompressor, pump, fläkt)
  | "heater"       // elvärmare
  | "stove"        // spis
  | "ev-charger"   // EV-laddare
  | "water-heater" // varmvattenberedare
  | "other";

/**
 * En enskild last kopplad till en rad. Flera laster per rad tillåts — ex:
 * "3 ljuspunkter + 2 uttag på samma krets". Auto-säkring summerar då total
 * effekt och väljer närmaste standardsäkring.
 */
export type RowLoad = {
  id: string;
  type: RowLoadType;
  /** Fritext-beskrivning, t.ex. "Köksspisfläkt" eller "Taklampa hall". */
  label?: string;
  /** Antal. Default = 1. */
  quantity?: number;
  /** Effekt per styck i W. Default per typ (ljuspunkt 18 W, uttag 2300 W …). */
  wattage?: number;
  /** Effektfaktor (cosφ). Default per typ (resistiv 1,0, motor 0,85). */
  cosPhi?: number;
};

/**
 * Tidsstämplad ögonblicksbild av panelen. Används för revisionshistorik —
 * användaren markerar "skapa revision" och vi snapshottar rows + meta.
 */
export type Revision = {
  id: string;
  createdAt: number;
  label: string;          // "Rev A", "Rev B", eller fritext
  note?: string;
  /** Snapshot-payload: JSON-serialiserad panel + sections + rows. */
  snapshot: string;
};

export type Panel = {
  id: PanelId;
  designation: string; // "CK1", "H1"
  title: string;       // "Huvudcentral lgh 3"
  location?: string;   // "El-rum källare"
  enclosure?: string;  // "ABB Mistral41W IP55"
  busbarAmps?: number; // märkström huvudbar
  /** Antal DIN-skenor i centralen (typ. 1-5). Undefined = inte specificerat. */
  railCount?: number;
  /** Antal TE-moduler per skena (typ. 12 / 18 / 24). Undefined = inte satt. */
  modulesPerRail?: number;
  /** Inkommande servis — jordningssystem. */
  earthingSystem?: EarthingSystem;
  /** Granskningsstatus. Default = "draft". */
  status?: PanelStatus;
  /** Projekt-metadata, syns i toolbar + titelblock i PDF. */
  designer?: string;     // Konstruktör / namn (fältnamnet "constructor" krockar med Object-prototype)
  company?: string;      // Företag
  phone?: string;        // Telefon
  projectNotes?: string; // Fri anteckning
  /** Revisioner — senaste sist. */
  revisions?: Revision[];
  createdAt: number;
  updatedAt: number;
};

export type Section = {
  id: SectionId;
  code: string;        // "S1"
  label?: string;      // "Belysning vardagsrum"
  order: number;       // sortnyckel
};

export type PanelRow = {
  id: PanelRowId;
  sectionId?: SectionId;  // null = osektionerad rad
  order: number;          // sortnyckel inom panelen
  designation: string;    // "F1", "F12"
  component?: string;     // kortkod eller partNumber från katalog
  ampRating?: number;     // 10, 16, 20, …
  poles?: 1 | 2 | 3 | 4;
  characteristic?: BreakerCharacteristic;
  sensitivity?: number;   // mA — endast RCD
  rcdType?: RcdType;
  cableType?: string;     // "EKK"
  cableCores?: number;    // 3, 5
  cableArea?: number;     // mm²
  cableLength?: number;   // m
  cableNumber?: string;   // "K101" — kabelnumrering för byggdokumentation
  usage?: string;         // "Belysning kök"
  loadWatts?: number;
  loadAmps?: number;
  notes?: string;
  /** 1-baserad DIN-skena komponenten sitter på. Undefined = inte placerad. */
  railNumber?: number;
  /** Markerad som granskad av elkonstruktör. Default = false. */
  reviewed?: boolean;
  /** Hierarki-nivå (0 = huvudrad, 1 = underrad, …). Default = 0. */
  indent?: number;
  /**
   * Laster på raden — flera objekt per rad (ljuspunkter, uttag, motorer).
   * När `fuseAuto` är sant räknas total effekt fram och ampRating/karakteristik
   * sätts automatiskt via `suggestFuse()` i `panelAutoFuse.ts`.
   */
  loads?: ReadonlyArray<RowLoad>;
  /**
   * När sant styr laster valet av säkring (ampRating, characteristic, component).
   * Användaren kan stänga av för att välja säkring manuellt trots laster-lista.
   */
  fuseAuto?: boolean;
};

export type PanelMaps = {
  /** En panel per Y.Doc i Centralspec-läget, men schemat tillåter fler. */
  panels: Y.Map<Y.Map<unknown>>;
  sections: Y.Map<Y.Map<unknown>>;
  rows: Y.Map<Y.Map<unknown>>;
};

export function getPanelMaps(doc: Y.Doc): PanelMaps {
  return {
    panels: doc.getMap<Y.Map<unknown>>("panels"),
    sections: doc.getMap<Y.Map<unknown>>("sections"),
    rows: doc.getMap<Y.Map<unknown>>("panelRows"),
  };
}

function toYMap(obj: Record<string, unknown>): Y.Map<unknown> {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) m.set(k, v);
  }
  return m;
}

function fromYMap<T>(m: Y.Map<unknown>): T {
  const out: Record<string, unknown> = {};
  m.forEach((v, k) => {
    out[k] = v;
  });
  return out as T;
}

// --- Panel ---
export function putPanel(maps: PanelMaps, panel: Panel): void {
  maps.panels.set(panel.id, toYMap(panel as unknown as Record<string, unknown>));
}
export function getPanel(maps: PanelMaps, id: PanelId): Panel | undefined {
  const m = maps.panels.get(id);
  return m ? fromYMap<Panel>(m) : undefined;
}
export function listPanels(maps: PanelMaps): Panel[] {
  return Array.from(maps.panels.values(), (m) => fromYMap<Panel>(m));
}
export function patchPanel(
  maps: PanelMaps,
  id: PanelId,
  patch: Partial<Omit<Panel, "id" | "createdAt">>,
): void {
  const m = maps.panels.get(id);
  if (!m) return;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) m.delete(k);
    else m.set(k, v);
  }
  m.set("updatedAt", Date.now());
}

// --- Section ---
export function putSection(maps: PanelMaps, section: Section): void {
  maps.sections.set(section.id, toYMap(section as unknown as Record<string, unknown>));
}
export function getSection(maps: PanelMaps, id: SectionId): Section | undefined {
  const m = maps.sections.get(id);
  return m ? fromYMap<Section>(m) : undefined;
}
export function listSections(maps: PanelMaps): Section[] {
  const arr = Array.from(maps.sections.values(), (m) => fromYMap<Section>(m));
  return arr.sort((a, b) => a.order - b.order);
}
export function deleteSection(maps: PanelMaps, id: SectionId): void {
  maps.sections.delete(id);
  // Rader i sektionen får `sectionId` satt till undefined så de blir osektionerade.
  maps.rows.forEach((m) => {
    if (m.get("sectionId") === id) m.delete("sectionId");
  });
}
export function patchSection(
  maps: PanelMaps,
  id: SectionId,
  patch: Partial<Omit<Section, "id">>,
): void {
  const m = maps.sections.get(id);
  if (!m) return;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) m.delete(k);
    else m.set(k, v);
  }
}

// --- Row ---
export function putRow(maps: PanelMaps, row: PanelRow): void {
  maps.rows.set(row.id, toYMap(row as unknown as Record<string, unknown>));
}
export function getRow(maps: PanelMaps, id: PanelRowId): PanelRow | undefined {
  const m = maps.rows.get(id);
  return m ? fromYMap<PanelRow>(m) : undefined;
}
export function listRows(maps: PanelMaps): PanelRow[] {
  const arr = Array.from(maps.rows.values(), (m) => fromYMap<PanelRow>(m));
  return arr.sort((a, b) => a.order - b.order);
}
export function patchRow(
  maps: PanelMaps,
  id: PanelRowId,
  patch: Partial<Omit<PanelRow, "id">>,
): void {
  const m = maps.rows.get(id);
  if (!m) return;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) m.delete(k);
    else m.set(k, v);
  }
}
export function deleteRow(maps: PanelMaps, id: PanelRowId): void {
  maps.rows.delete(id);
}

// --- Factories ---
export function newPanel(init: Partial<Panel> = {}): Panel {
  const now = Date.now();
  return {
    id: panelId(newId("panel")),
    designation: init.designation ?? "CK1",
    title: init.title ?? "Ny central",
    location: init.location,
    enclosure: init.enclosure,
    busbarAmps: init.busbarAmps,
    railCount: init.railCount,
    modulesPerRail: init.modulesPerRail,
    createdAt: now,
    updatedAt: now,
  };
}

export function newSection(code: string, order: number, label?: string): Section {
  return { id: sectionId(newId("sect")), code, label, order };
}

export function newRow(order: number, designation: string): PanelRow {
  return { id: panelRowId(newId("row")), order, designation };
}
